import { useCallback, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  Award,
  Building2,
  CalendarDays,
  CheckSquare,
  FileText,
  LayoutDashboard,
  MessageCircle,
  MessageSquare,
  Network,
  Receipt,
  StickyNote,
} from "lucide-react";
import { Card, Button } from "@/design-system/components/ui";
import { Table, THead, TBody, TR, TH, TD } from "@/design-system/components/ui";
import { Textarea } from "@/components/ui/Select";
import {
  EntityTabBar,
  type EntityTabItem,
} from "@/components/layout/EntityTabBar";
import { DocumentPanel } from "@/pages/documents/components/DocumentPanel";
import { TaskListMini } from "@/pages/tasks/components/TaskListMini";
import { AppointmentListMini } from "@/pages/calendar/components/AppointmentListMini";
import { CommunicationsTabPanel } from "@/pages/communications/components/CommunicationsTabPanel";
import { NotesTabPanel } from "@/pages/notes/components/NotesTabPanel";
import { ActivityTabPanel } from "@/pages/activity/components/ActivityTabPanel";
import { RelatedConversationsPanel } from "@/components/messaging/RelatedConversationsPanel";
import { TabPlaceholderCard } from "@/components/DealFlow/common/TabPlaceholderCard";
import { useToast } from "@/components/ui/Toast";
import { useAuth } from "@/hooks/useAuth";
import { extractErrorMessage } from "@/api/client";
import { entitiesApi } from "@/api/counterparties";
import {
  useProvider,
  usePoolsServedByProvider,
  useUpdateProvider,
} from "@/hooks/capitalMarkets/useServiceProviders";
import type { AnyServiceProviderDto } from "@/types/serviceProviders";
import { PoolStatusBadge, ProviderStatusBadge } from "../shared/components";
import { formatDate } from "../shared/format";
import type { ProviderConfig } from "./providerConfig";

type TabId =
  | "overview"
  | "pools"
  | "fees"
  | "certifications"
  | "documents"
  | "tasks"
  | "calendar"
  | "messages"
  | "communications"
  | "notes"
  | "activity";

const TABS: readonly TabId[] = [
  "overview",
  "pools",
  "fees",
  "certifications",
  "documents",
  "tasks",
  "calendar",
  "messages",
  "communications",
  "notes",
  "activity",
];

const TAB_ITEMS: ReadonlyArray<EntityTabItem<TabId>> = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "pools", label: "Pools Served", icon: Network },
  { key: "fees", label: "Fee Schedule", icon: Receipt },
  { key: "certifications", label: "Certifications", icon: Award },
  { key: "documents", label: "Documents", icon: FileText },
  { key: "tasks", label: "Tasks", icon: CheckSquare },
  { key: "calendar", label: "Calendar", icon: CalendarDays },
  { key: "messages", label: "Messages", icon: MessageSquare },
  { key: "communications", label: "Communications", icon: MessageCircle },
  { key: "notes", label: "Notes", icon: StickyNote },
  { key: "activity", label: "Activity", icon: Activity },
];

function isTabId(value: string | null): value is TabId {
  return value !== null && (TABS as readonly string[]).includes(value);
}

/** Provider-specific Overview fields. */
function providerFields(p: AnyServiceProviderDto): { label: string; value: string }[] {
  const out: { label: string; value: string }[] = [];
  if ("registrationNumber" in p) {
    out.push({ label: "Registration number", value: p.registrationNumber ?? "—" });
    out.push({ label: "Agreement date", value: formatDate(p.agreementDate) });
  } else if ("jurisdiction" in p) {
    out.push({ label: "Jurisdiction", value: p.jurisdiction });
    out.push({ label: "Agreement date", value: formatDate(p.agreementDate) });
  } else if ("finraCrdNumber" in p) {
    out.push({ label: "FINRA CRD number", value: p.finraCrdNumber ?? "—" });
    out.push({ label: "APA agreement date", value: formatDate(p.apaAgreementDate) });
  } else if ("triggerConditionsJson" in p) {
    out.push({ label: "Appointment date", value: formatDate(p.appointmentDate) });
    out.push({
      label: "Operational readiness last tested",
      value: formatDate(p.operationalReadinessLastTested),
    });
  }
  return out;
}

export function ServiceProviderDetailPage({
  config,
}: {
  config: ProviderConfig;
}) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const providerQuery = useProvider(config.type, id);
  const provider = providerQuery.data as AnyServiceProviderDto | undefined;

  const entityQuery = useQuery({
    queryKey: ["counterparties", "entity", provider?.entityId],
    queryFn: () => entitiesApi.get(provider!.entityId),
    enabled: !!provider?.entityId,
  });
  const name = entityQuery.data?.legalEntityName ?? config.singularLabel;

  const [params, setParams] = useSearchParams();
  const tabParam = params.get("tab");
  const tab: TabId = isTabId(tabParam) ? tabParam : "overview";

  const setTab = useCallback(
    (next: TabId) => {
      const updated = new URLSearchParams(params);
      if (next === "overview") updated.delete("tab");
      else updated.set("tab", next);
      setParams(updated, { replace: true });
    },
    [params, setParams],
  );

  if (providerQuery.isLoading) {
    return (
      <Card>
        <p className="text-small text-gray-mid italic m-0">
          Loading {config.singularLabel.toLowerCase()}…
        </p>
      </Card>
    );
  }

  if (!provider) {
    return (
      <Card>
        <h2 className="font-display font-bold text-h2">
          {config.singularLabel} not found
        </h2>
        <button
          type="button"
          className="mt-space-3 text-gold-shadow hover:underline text-small"
          onClick={() => navigate(config.routeBase.replace(/\/[^/]+$/, "/service-providers"))}
        >
          Back to service providers
        </button>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-space-3">
      <div className="flex flex-wrap items-center gap-space-3">
        <h1 className="font-display font-bold text-h2 text-onyx m-0">{name}</h1>
        <span className="text-caption font-display uppercase tracking-label text-gray-mid">
          {config.singularLabel}
        </span>
        <ProviderStatusBadge status={provider.status} />
      </div>

      <EntityTabBar
        ariaLabel="Service provider sections"
        testIdPrefix="service-provider"
        items={TAB_ITEMS}
        value={tab}
        onChange={setTab}
      />

      <div className="pt-space-4">
        {tab === "overview" && (
          <OverviewTab provider={provider} config={config} name={name} />
        )}
        {tab === "pools" && (
          <PoolsServedTab config={config} providerId={provider.id} />
        )}
        {tab === "fees" && (
          <FeeScheduleTab config={config} provider={provider} />
        )}
        {tab === "certifications" && (
          <TabPlaceholderCard
            title="Certifications"
            body="Service-provider certifications surface here once the certifications module registers these entity types."
          />
        )}
        {tab === "documents" && (
          <DocumentPanel
            ownerType={config.ownerType}
            ownerId={provider.id}
            rootName={name}
          />
        )}
        {tab === "tasks" && (
          <TaskListMini
            relatedEntityType={config.taskType}
            relatedEntityId={provider.id}
            addButtonLabel="+ Add task"
          />
        )}
        {tab === "calendar" && (
          <AppointmentListMini
            relatedEntityType={config.taskType}
            relatedEntityId={provider.id}
            entityLabel={name}
          />
        )}
        {tab === "messages" && (
          <RelatedConversationsPanel
            resourceType={config.messagingType}
            resourceId={provider.id}
            entityLabel={name}
          />
        )}
        {tab === "communications" && (
          <CommunicationsTabPanel
            ownerType={config.ownerType}
            ownerId={provider.id}
            ownerLabel={name}
          />
        )}
        {tab === "notes" && (
          <NotesTabPanel ownerType={config.ownerType} ownerId={provider.id} />
        )}
        {tab === "activity" && (
          <ActivityTabPanel ownerType={config.ownerType} ownerId={provider.id} />
        )}
      </div>
    </div>
  );
}

function OverviewTab({
  provider,
  config,
  name,
}: {
  provider: AnyServiceProviderDto;
  config: ProviderConfig;
  name: string;
}) {
  const fields = providerFields(provider);
  return (
    <Card>
      <h3 className="font-display font-bold text-h3 m-0 mb-space-3 flex items-center gap-space-2">
        <Building2 size={16} aria-hidden /> Identity
      </h3>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-space-3 m-0">
        <Field label="Name" value={name} />
        <div className="flex flex-col">
          <dt className="text-label font-display uppercase tracking-label text-gray-mid">
            Linked Entity
          </dt>
          <dd className="text-small m-0">
            <Link
              to={`/counterparties/entities/${provider.entityId}`}
              className="text-gold-shadow hover:underline"
            >
              Entity detail
            </Link>
          </dd>
        </div>
        <Field label="Provider type" value={config.singularLabel} />
        {fields.map((f) => (
          <Field key={f.label} label={f.label} value={f.value} />
        ))}
      </dl>
      {"activationProceduresSummary" in provider &&
        provider.activationProceduresSummary && (
          <div className="mt-space-3">
            <h4 className="text-label font-display uppercase tracking-label text-gray-mid m-0">
              Activation procedures
            </h4>
            <p className="text-small text-onyx m-0 mt-space-1 whitespace-pre-line">
              {provider.activationProceduresSummary}
            </p>
          </div>
        )}
      {provider.notes && (
        <p className="text-small text-gray-mid mt-space-3 mb-0 whitespace-pre-line">
          {provider.notes}
        </p>
      )}
    </Card>
  );
}

function PoolsServedTab({
  config,
  providerId,
}: {
  config: ProviderConfig;
  providerId: string;
}) {
  const query = usePoolsServedByProvider(config.type, providerId);
  const pools = query.data ?? [];

  if (query.isLoading) {
    return (
      <Card>
        <p className="text-small text-gray-mid italic m-0">Loading pools…</p>
      </Card>
    );
  }

  return (
    <Card>
      <h3 className="font-display font-bold text-h3 m-0 mb-space-3">
        Pools Served
      </h3>
      {pools.length === 0 ? (
        <p className="text-small text-gray-mid italic m-0">
          This {config.singularLabel.toLowerCase()} is not engaged on any pool
          yet.
        </p>
      ) : (
        <Table>
          <THead>
            <TR>
              <TH>Pool</TH>
              <TH>Status</TH>
              <TH>Role</TH>
            </TR>
          </THead>
          <TBody>
            {pools.map((p) => (
              <TR key={p.spvPoolId}>
                <TD>
                  <Link
                    to={`/capital-markets/pools/${p.spvPoolId}`}
                    className="font-semibold hover:text-gold-shadow"
                  >
                    {p.poolName}
                  </Link>
                </TD>
                <TD>
                  <PoolStatusBadge status={p.status} />
                </TD>
                <TD>
                  {p.isPoolOverride
                    ? "Pool-specific override"
                    : p.isTrustDefault
                      ? "SPV trust default"
                      : "—"}
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}
    </Card>
  );
}

function FeeScheduleTab({
  config,
  provider,
}: {
  config: ProviderConfig;
  provider: AnyServiceProviderDto;
}) {
  const { hasPermission } = useAuth();
  const toast = useToast();
  const canManage = hasPermission(config.managePermission);
  const update = useUpdateProvider(config.type, provider.id);

  const isBackup = config.type === "BackupServicer";
  const current = isBackup
    ? (provider as { standbyFeeScheduleJson: string }).standbyFeeScheduleJson
    : (provider as { feeScheduleJson: string }).feeScheduleJson;

  const [draft, setDraft] = useState(() => formatJson(current));

  const handleSave = () => {
    let parsed: string;
    try {
      parsed = JSON.stringify(JSON.parse(draft));
    } catch {
      toast.error("Fee schedule must be valid JSON.");
      return;
    }
    const body = isBackup
      ? { standbyFeeScheduleJson: parsed }
      : { feeScheduleJson: parsed };
    update
      .mutateAsync(body)
      .then(() => toast.success("Fee schedule saved."))
      .catch((err) => toast.error(extractErrorMessage(err)));
  };

  return (
    <Card>
      <h3 className="font-display font-bold text-h3 m-0 mb-space-2">
        Fee Schedule
      </h3>
      <p className="text-caption text-gray-mid mb-space-3">
        Fee terms are stored as loose JSON for v1.0 (spec §12 Q6). Edit the
        document below; it is validated as JSON on save.
      </p>
      <Textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        rows={12}
        className="font-mono text-caption"
        disabled={!canManage}
        aria-label="Fee schedule JSON"
      />
      {canManage && (
        <div className="flex justify-end mt-space-3">
          <Button size="sm" loading={update.isPending} onClick={handleSave}>
            Save fee schedule
          </Button>
        </div>
      )}
    </Card>
  );
}

function formatJson(raw: string | null | undefined): string {
  if (!raw) return "{}";
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <dt className="text-label font-display uppercase tracking-label text-gray-mid">
        {label}
      </dt>
      <dd className="text-small text-onyx m-0">{value}</dd>
    </div>
  );
}
